import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';
import { Select2Field } from '@/components/forms/Select2Field';
import type { SelectOption } from '@/components/forms/SelectField';
import { SPREAD_BET } from '@/api/types/globalSymbols';
import type { GlobalSymbolFormDataDto } from '@/api/types/globalSymbols';

export interface GlobalSymbolFormValues {
  symbol: string;
  typeName: string;
  sbUnderlyingCurrency: string;
  assetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  quoteAssetClass: string;
  countryName: string;
  contractSize: string;
  digits: string;
}

// Legacy validation: required = Instrument type, Asset Class, Base, Quote,
// Quote Asset Class, Contract Size (> 0). Create also requires Symbol.
const baseSchema = z.object({
  symbol: z.string(),
  typeName: z.string().trim().min(1, 'Instrument type is required.'),
  sbUnderlyingCurrency: z.string(),
  assetClass: z.string().trim().min(1, 'Asset Class is required.'),
  baseCurrency: z.string().trim().min(1, 'Base is required.'),
  quoteCurrency: z.string().trim().min(1, 'Quote is required.'),
  quoteAssetClass: z.string().trim().min(1, 'Quote Asset Class is required.'),
  countryName: z.string(),
  contractSize: z
    .string()
    .refine((v) => Number(String(v).replace(/,/g, '')) > 0, 'Contract Size is required.'),
  digits: z.string(),
});

interface GlobalSymbolFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  formData: GlobalSymbolFormDataDto;
  initial?: Partial<GlobalSymbolFormValues>;
  busy?: boolean;
  onSubmit: (values: GlobalSymbolFormValues) => void;
  onClose: () => void;
}

export function GlobalSymbolFormModal({
  show,
  mode,
  formData,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: GlobalSymbolFormModalProps) {
  const schema =
    mode === 'create'
      ? baseSchema.refine((v) => v.symbol.trim().length > 0, {
          path: ['symbol'],
          message: 'Symbol is required.',
        })
      : baseSchema;

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<GlobalSymbolFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      symbol: initial?.symbol ?? '',
      typeName: initial?.typeName ?? '',
      sbUnderlyingCurrency: initial?.sbUnderlyingCurrency ?? '',
      assetClass: initial?.assetClass ?? '',
      baseCurrency: initial?.baseCurrency ?? '',
      quoteCurrency: initial?.quoteCurrency ?? '',
      quoteAssetClass: initial?.quoteAssetClass ?? '',
      countryName: initial?.countryName ?? '',
      contractSize: initial?.contractSize ?? '',
      digits: initial?.digits ?? '',
    },
  });

  const typeOptions: SelectOption[] = formData.symbolTypeList.map((t) => ({
    value: t.name,
    label: t.name,
  }));
  const assetClassOptions: SelectOption[] = formData.assetClassList.map((a) => ({
    value: a,
    label: a,
  }));
  const countryOptions: SelectOption[] = formData.countryList.map((c) => ({ value: c, label: c }));

  const selectedType = watch('typeName');
  const sbEnabled = selectedType === SPREAD_BET;

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'Add New Global Symbol' : 'Edit Global Symbol'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="global-symbol-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="global-symbol-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {mode === 'create' ? (
          <TextField
            label="Instrument"
            autoComplete="off"
            error={errors.symbol?.message}
            {...register('symbol')}
          />
        ) : (
          <div className="mb-3">
            <label className="form-label">Instrument</label>
            <input className="form-control" value={initial?.symbol ?? ''} disabled readOnly />
          </div>
        )}

        <Select2Field
          control={control}
          name="typeName"
          label="Instrument type"
          placeholder=""
          options={typeOptions}
        />

        <div className="mb-3">
          <label htmlFor="sbUnderlyingCurrency" className="form-label">
            SB underlying currency
          </label>
          <input
            id="sbUnderlyingCurrency"
            className="form-control"
            autoComplete="off"
            disabled={!sbEnabled}
            {...register('sbUnderlyingCurrency')}
          />
        </div>

        <Select2Field
          control={control}
          name="assetClass"
          label="Asset Class"
          placeholder=""
          options={assetClassOptions}
        />

        <TextField
          label="Base"
          autoComplete="off"
          error={errors.baseCurrency?.message}
          {...register('baseCurrency')}
        />

        <TextField
          label="Quote"
          autoComplete="off"
          error={errors.quoteCurrency?.message}
          {...register('quoteCurrency')}
        />

        <Select2Field
          control={control}
          name="quoteAssetClass"
          label="Quote Asset Class"
          placeholder=""
          options={assetClassOptions}
        />

        <Select2Field
          control={control}
          name="countryName"
          label="Country"
          placeholder=""
          options={countryOptions}
        />

        <TextField
          label="Contract Size"
          autoComplete="off"
          error={errors.contractSize?.message}
          {...register('contractSize')}
        />

        <TextField label="Digits" autoComplete="off" error={errors.digits?.message} {...register('digits')} />
      </form>
    </Modal>
  );
}
